import * as React from "react";
import { Box } from "@mui/material";

export function TableWithOffsetScrollbar({
  fixedWidth,
  scrollRef,
}: {
  fixedWidth: number;
  scrollRef: React.RefObject<HTMLDivElement>;
}) {
  const barRef = React.useRef<HTMLDivElement | null>(null);
  const spacerRef = React.useRef<HTMLDivElement | null>(null);
  const syncing = React.useRef<"bar" | "content" | null>(null);

  // Match fake scrollbar width to table scroll width
  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    const spacer = spacerRef.current;
    if (!el || !spacer) return;

    const syncWidth = () => {
      spacer.style.width = `${el.scrollWidth - fixedWidth}px`;
    };

    syncWidth();
    const ro = new ResizeObserver(syncWidth);
    ro.observe(el);

    return () => ro.disconnect();
  }, [scrollRef, fixedWidth]);

  // Sync scrolling
  React.useEffect(() => {
    const content = scrollRef.current;
    const bar = barRef.current;
    if (!content || !bar) return;

    const onBarScroll = () => {
      if (syncing.current === "content") return;
      syncing.current = "bar";
      content.scrollLeft = bar.scrollLeft;
      syncing.current = null;
    };

    const onContentScroll = () => {
      if (syncing.current === "bar") return;
      syncing.current = "content";
      bar.scrollLeft = content.scrollLeft;
      syncing.current = null;
    };

    bar.addEventListener("scroll", onBarScroll, { passive: true });
    content.addEventListener("scroll", onContentScroll, { passive: true });

    return () => {
      bar.removeEventListener("scroll", onBarScroll);
      content.removeEventListener("scroll", onContentScroll);
    };
  }, [scrollRef]);

  React.useEffect(() => {
    const content = scrollRef.current;
    const bar = barRef.current;
    if (!content || !bar) return;

    const onWheel = () => {
      // Force-sync after wheel scrolling (Shift + wheel)
      requestAnimationFrame(() => {
        bar.scrollLeft = content.scrollLeft;
      });
    };

    content.addEventListener("wheel", onWheel, { passive: true });

    return () => {
      content.removeEventListener("wheel", onWheel);
    };
  }, [scrollRef]);

  return (
    <Box sx={{ display: "flex" }}>
      <Box sx={{ width: fixedWidth, flex: "0 0 auto" }} />
      <Box
        ref={barRef}
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          minHeight: 12,
          display: "block",
          "&::-webkit-scrollbar": {
            height: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#999",
            borderRadius: 4,
          },
        }}
      >
        <Box ref={spacerRef} />
      </Box>
    </Box>
  );
}
